import { Animated, StyleSheet } from "react-native"
import { type AnalysisType, type UserType } from "@/generated/graphql"
import { createBranchAnalysisShareLink } from "@/stores/branch"
import * as AnalyzerEvents from "@/analytics/events/analyzer"
import { useShare } from "@/hooks/useShare"
import { SubjectPropertyOverlayText } from "@/components/analysis/header/SubjectPropertyOverlayText"
import { type FC } from "react"
import { SubjectSelectionButtons } from "@/components/analysis/header/SubjectSelectionButtons"
import { Image } from "expo-image"
import { Sentry } from "@/hooks/sentry"
import {
  useSetFavoriteAnalysis,
  useSetAnalysisNotification,
} from "@/queries/analysis"
import {
  favoritesActionClicked,
  notificationActionClicked,
} from "@/analytics/events/properties"
import { showSuccessToast } from "@/components/toasts/AppToast"

interface StretchyHeaderProps {
  scrollY: Animated.Value
  user: UserType
  analysis: AnalysisType
  compactHeaderIsActive: boolean
  onEditPress: () => void
  onStreetPress: () => void
}

export const HEADER_HEIGHT = 200

const StretchyHeader: FC<StretchyHeaderProps> = ({
  scrollY,
  user,
  analysis,
  compactHeaderIsActive,
  onEditPress,
  onStreetPress,
}) => {
  const share = useShare()
  const analysisNotificationMutation = useSetAnalysisNotification()
  const favoriteAnalysisMutation = useSetFavoriteAnalysis()
  const shareAnalysis = async () => {
    const street = analysis.property.street
    const branchUrl = await createBranchAnalysisShareLink({
      user,
      analysisId: analysis.id,
      street,
      imageUrl: analysis.property.heroImage ?? "",
    })

    await share({
      message: `Check out the RE investment analysis I've created using the Backflip app for ${street}.`,
      url: branchUrl,
      title: `Backflip Analysis for ${street}`,
      subject: `Backflip Analysis for ${street}`,
    })
  }

  const headerOpacity = scrollY.interpolate({
    inputRange: [-30, 0, 50],
    outputRange: [0.0, 1, 0.0],
    extrapolate: "clamp",
  })

  const headerScale = scrollY.interpolate({
    inputRange: [-100, 0, 200],
    outputRange: [1.5, 1, 1], // Begin to scale down slightly when the user starts scrolling up
    extrapolate: "clamp",
  })

  const headerTranslateY = scrollY.interpolate({
    inputRange: [-100, 0],
    outputRange: [-50, 0], // Move the header up at half the speed of the scroll up
    extrapolate: "clamp",
  })

  // Combine the scale and translate transforms into the header's animated styles
  const animatedHeaderStyles: any = {
    transform: [
      { scaleX: headerScale },
      { scaleY: headerScale },
      { translateY: headerTranslateY },
    ],
  }

  const heroImageUri: string = analysis.property.heroImage ?? ""

  function toggleNotifications() {
    const notify = !analysis.notificationsEnabled
    analysis.notificationsEnabled = notify
    notificationActionClicked(analysis.id, "Subject Property", notify)

    analysisNotificationMutation.mutate(
      { analysisId: analysis.id, enableNotifications: notify },
      {
        onSuccess: () => {
          if (notify) {
            showSuccessToast({
              title: "Great!",
              message:
                "We’ll send you an email when there are changes to the comps for this property.",
              autoHide: true,
              visibilityTime: 10000,
            })
          }
        },

        onError: (err) => {
          console.error("Toggle Analysis Notification Error:", err)
        },
      }
    )
  }

  function favoriteAnalysis() {
    const favorite = !analysis.isFavorite
    analysis.isFavorite = favorite
    favoritesActionClicked("Subject Property", favorite)

    favoriteAnalysisMutation.mutate(
      { analysisId: analysis.id, favorite },
      {
        onError: (err) => {
          console.error(err)
        },
      }
    )
  }

  return (
    <Animated.View style={[styles.header, animatedHeaderStyles]}>
      <Image
        source={{ uri: heroImageUri }}
        contentFit={"cover"}
        style={styles.stretchyImage}
        onError={(e) => {
          console.error("Image loading error:", e.error, heroImageUri)
          Sentry.captureException(e)
        }}
      />
      <Animated.View
        style={{
          // NOTE: Animated.View does not work with className prop on web
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          paddingVertical: 8,
          paddingHorizontal: 16,
          opacity: headerOpacity,
          justifyContent: "flex-end",
        }}
      >
        <SubjectPropertyOverlayText
          onEditPress={onEditPress}
          analysis={analysis}
          compactHeaderIsActive={compactHeaderIsActive}
        />
        <SubjectSelectionButtons
          displaysNotificationIcon={analysis.isNotificationsEligible || false}
          notificationsEnabled={analysis.notificationsEnabled || false}
          onNotify={() => {
            toggleNotifications()
          }}
          isFavorite={analysis.isFavorite || false}
          onFavorite={favoriteAnalysis}
          onShare={() => {
            if (analysis) {
              AnalyzerEvents.shareClicked(analysis)
            }
            void shareAnalysis()
          }}
          onStreetView={onStreetPress}
        />
      </Animated.View>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  header: {
    height: HEADER_HEIGHT,
  },
  stretchyImage: {
    width: "100%",
    height: "100%",
  },
})

export default StretchyHeader
